/**
 * `/entry/lite` (Lite版データ手入力)。CSV取込を経由せず画面から月次PL・仕入先別コスト・
 * 枠外調整を直接入力・更新・削除できるようにする。
 *
 * 月次PL/仕入先別コストは店舗×月=完全置換方式 (apps/api/src/routes/lite-csv-import.ts) のため、
 * 既存データを読み込んだ状態で編集・追加・削除してから保存する (ManualEntryPage.tsx と同型UXパターン)。
 * ただし保存経路はCSV取込のpreview/commitとは別 (POST /lite-csv-import/:type/direct-save)。
 * この画面は既にマスタから選択済みのtarget_idを持っているため、CSV化してname→id解決
 * (csv_column_mappings 参照) を経由する必要が無い。実機検証で「CSV取込を一度もしていない
 * (csv_column_mappingsが空の) テナントでは手入力すら保存できない」不具合が判明したため、
 * target_idを直接送るエンドポイントに変更した。
 * 枠外調整 (monthly_manual_adjustments) はCSV取込経路自体が存在しない手入力専用テーブルのため、
 * 新設した /lite-manual-adjustments CRUD APIを直接叩く。
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, AlertTriangle, Info, Pencil } from 'lucide-react';
import { MonthPicker } from '@/components/MonthPicker';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Modal, ConfirmDialog } from '@/components/Modal';

type Store = { store_id: string; store_code: string; store_name: string; status: 'active' | 'closed' };
type MasterItem = { id: string; name: string };
type RecordEntry = { target_id: string; target_name: string; amount: number };
type RecordsResponse = { node_id: string; month: string; entries: RecordEntry[] };
type DirectSaveResponse = { status: 'completed' | 'failed'; committed_store_months: number; total_store_months: number; error?: string };

type SourceType = 'monthly_pl' | 'monthly_supplier_costs';
type EntryRow = { rowKey: number; target_id: string; amount: string };

const SOURCE_LABEL: Record<SourceType, string> = {
  monthly_pl: '月次PL (勘定科目別)',
  monthly_supplier_costs: '月次仕入先別コスト',
};
const TARGET_LABEL: Record<SourceType, string> = {
  monthly_pl: '勘定科目',
  monthly_supplier_costs: '仕入先',
};

const currentYearMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

let rowKeySeq = 0;
const emptyEntryRow = (): EntryRow => ({ rowKey: ++rowKeySeq, target_id: '', amount: '' });

// -----------------------------------------------------------------------------
// 枠外調整 (monthly_manual_adjustments)
// -----------------------------------------------------------------------------
type Adjustment = {
  id: string; node_id: string; sales_month: string; adjustment_type: string;
  amount: number; note: string; applied_to_kpi: boolean;
};
const ADJUSTMENT_TYPE_LABEL: Record<string, string> = {
  profit_correction: '損益修正', inter_store_transfer: '店舗間付替', hq_burden: '本部負担', other: 'その他',
};
type AdjustmentForm = { adjustment_type: string; amount: string; note: string; applied_to_kpi: boolean };
const emptyAdjustmentForm = (): AdjustmentForm => ({ adjustment_type: 'other', amount: '', note: '', applied_to_kpi: true });

function AdjustmentsSection({ storeId, monthDate }: { storeId: string; monthDate: string }) {
  const qc = useQueryClient();
  const queryKey = ['lite-manual-adjustments', storeId, monthDate];
  const { data: adjustments = [], isLoading } = useQuery<Adjustment[]>({
    queryKey,
    queryFn: () => apiFetch<Adjustment[]>(`/lite-manual-adjustments?node_id=${storeId}&month=${monthDate}`),
    enabled: Boolean(storeId && monthDate),
  });

  const [editing, setEditing] = useState<{ id: string | null; form: AdjustmentForm } | null>(null);
  const [deleting, setDeleting] = useState<Adjustment | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const amount = Number(editing.form.amount);
      if (!Number.isFinite(amount)) throw new Error('金額は数値で入力してください');
      if (!editing.form.note.trim()) throw new Error('内容を入力してください');
      const body = {
        node_id: storeId, sales_month: monthDate, adjustment_type: editing.form.adjustment_type,
        amount, note: editing.form.note.trim(), applied_to_kpi: editing.form.applied_to_kpi,
      };
      if (editing.id) {
        await apiFetch(`/lite-manual-adjustments/${editing.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await apiFetch('/lite-manual-adjustments', { method: 'POST', body: JSON.stringify(body) });
      }
    },
    onSuccess: () => {
      toast.success('保存しました');
      setEditing(null);
      qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : (e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/lite-manual-adjustments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('削除しました');
      setDeleting(null);
      qc.invalidateQueries({ queryKey });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : (e as Error).message),
  });

  return (
    <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">枠外調整</h3>
          <p className="text-xs text-neutral-sub mt-0.5">損益修正・店舗間付替・本部負担分等、CSV取込対象外の調整をこの店舗・月に紐づけて登録します</p>
        </div>
        <button type="button" onClick={() => setEditing({ id: null, form: emptyAdjustmentForm() })}
          className="flex items-center gap-1 px-3 py-1.5 rounded bg-brand-red text-white text-xs hover:bg-brand-red-hover">
          <Plus size={14} /> 追加
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-neutral-sub py-4 text-center">読込中…</div>
      ) : adjustments.length === 0 ? (
        <div className="text-sm text-neutral-sub py-4 text-center">登録された枠外調整はありません</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-2 py-2">種別</th>
                <th className="text-left px-2 py-2">金額</th>
                <th className="text-left px-2 py-2">内容</th>
                <th className="text-left px-2 py-2">KPI反映</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => (
                <tr key={a.id} className="border-t border-neutral-line">
                  <td className="px-2 py-1.5">{ADJUSTMENT_TYPE_LABEL[a.adjustment_type] ?? a.adjustment_type}</td>
                  <td className="px-2 py-1.5">{a.amount.toLocaleString()}</td>
                  <td className="px-2 py-1.5">{a.note}</td>
                  <td className="px-2 py-1.5">{a.applied_to_kpi ? '反映する' : '反映しない'}</td>
                  <td className="px-2 py-1.5 flex gap-1">
                    <button type="button" onClick={() => setEditing({
                      id: a.id,
                      form: { adjustment_type: a.adjustment_type, amount: String(a.amount), note: a.note, applied_to_kpi: a.applied_to_kpi },
                    })} className="text-neutral-sub hover:text-brand-red p-1" aria-label="編集"><Pencil size={14} /></button>
                    <button type="button" onClick={() => setDeleting(a)} className="text-neutral-sub hover:text-warning-text p-1" aria-label="削除"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!editing} title={editing?.id ? '枠外調整を編集' : '枠外調整を追加'} size="sm" onClose={() => setEditing(null)}
        footer={
          <>
            <button type="button" onClick={() => setEditing(null)} disabled={saveMut.isPending}
              className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold hover:bg-surface-sunken disabled:opacity-45">キャンセル</button>
            <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
              className="h-10 px-5 rounded-md bg-brand-red text-white text-[13px] font-extrabold hover:bg-brand-red-hover disabled:opacity-45">
              {saveMut.isPending ? '保存中…' : '保存する'}
            </button>
          </>
        }>
        {editing && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-neutral-sub mb-1">種別</label>
              <select value={editing.form.adjustment_type}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, adjustment_type: e.target.value } })}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring">
                {Object.entries(ADJUSTMENT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-sub mb-1">金額 (円、マイナス可)</label>
              <input type="number" value={editing.form.amount}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, amount: e.target.value } })}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring" />
            </div>
            <div>
              <label className="block text-xs text-neutral-sub mb-1">内容</label>
              <textarea value={editing.form.note}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, note: e.target.value } })}
                maxLength={400} rows={3}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.form.applied_to_kpi}
                onChange={(e) => setEditing({ ...editing, form: { ...editing.form, applied_to_kpi: e.target.checked } })} />
              ダッシュボードのKPI集計に反映する
            </label>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleting} title="枠外調整を削除しますか？" destructive
        confirmLabel="削除する" loading={deleteMut.isPending}
        onClose={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
        message={<p>{deleting?.note} ({deleting?.amount.toLocaleString()}円) を削除します。この操作は取り消せません。</p>} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// メイン画面
// -----------------------------------------------------------------------------
export function LiteManualEntryPage() {
  const qc = useQueryClient();
  const [storeId, setStoreId] = useState('');
  const [month, setMonth] = useState(currentYearMonth());
  const [sourceType, setSourceType] = useState<SourceType>('monthly_pl');
  const [rows, setRows] = useState<EntryRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 「この店舗×月×種別の組み合わせで既存データを読み込んだか」を記録し、読込完了前の保存や
  // 再フェッチによるユーザー入力の上書きを防ぐ (ManualEntryPage.tsx と同型パターン)
  const loadedKeyRef = useRef<string | null>(null);

  const { data: stores = [] } = useQuery<Store[]>({ queryKey: ['stores'], queryFn: () => apiFetch<Store[]>('/stores') });
  const { data: categories = [] } = useQuery<MasterItem[]>({
    queryKey: ['account-categories'], queryFn: () => apiFetch<MasterItem[]>('/account-categories'), enabled: sourceType === 'monthly_pl',
  });
  const { data: suppliers = [] } = useQuery<MasterItem[]>({
    queryKey: ['suppliers-lite'], queryFn: () => apiFetch<MasterItem[]>('/suppliers-lite'), enabled: sourceType === 'monthly_supplier_costs',
  });
  const masterList = sourceType === 'monthly_pl' ? categories : suppliers;

  const monthDate = month ? `${month}-01` : '';
  const loadKey = `${sourceType}::${storeId}::${monthDate}`;

  const existingQuery = useQuery<RecordsResponse>({
    queryKey: ['lite-manual-entry-records', sourceType, storeId, monthDate],
    queryFn: () => apiFetch<RecordsResponse>(`/lite-csv-import/${sourceType}/records?node_id=${storeId}&month=${monthDate}`),
    enabled: Boolean(storeId && monthDate),
  });

  useEffect(() => {
    if (!storeId || !monthDate || !existingQuery.data) return;
    if (loadedKeyRef.current === loadKey) return;
    loadedKeyRef.current = loadKey;
    setRows(existingQuery.data.entries.map((e) => ({ rowKey: ++rowKeySeq, target_id: e.target_id, amount: String(e.amount) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, monthDate, sourceType, existingQuery.data]);

  useEffect(() => {
    if (!storeId || !monthDate) { setRows([]); loadedKeyRef.current = null; }
  }, [storeId, monthDate]);

  const selectedStore = stores.find((s) => s.store_id === storeId);
  const addRow = () => setRows((prev) => [...prev, emptyEntryRow()]);
  const removeRow = (rowKey: number) => setRows((prev) => prev.filter((r) => r.rowKey !== rowKey));
  const updateRow = (rowKey: number, field: 'target_id' | 'amount', value: string) => {
    setRows((prev) => prev.map((r) => (r.rowKey === rowKey ? { ...r, [field]: value } : r)));
  };

  const filledRows = rows.filter((r) => r.target_id && r.amount.trim() !== '');
  const dataReady = Boolean(storeId && monthDate) && loadedKeyRef.current === loadKey && !existingQuery.isFetching;
  // QAレビュー MEDIUM反映: 総件数の比較だけだと「3件→3件(1件削除+1件追加)」のような
  // 同数入替えで削除が見逃される。実際に消えるtarget_idの集合差分で判定する
  const filledTargetIds = new Set(filledRows.map((r) => r.target_id));
  const removedCount = (existingQuery.data?.entries ?? []).filter((e) => !filledTargetIds.has(e.target_id)).length;

  const openConfirm = () => {
    if (!storeId || !selectedStore) { toast.error('店舗を選択してください'); return; }
    if (!month) { toast.error('対象月を選択してください'); return; }
    if (!dataReady) { toast.error('既存データの読込が完了していません。少し待ってから再度お試しください'); return; }
    // Testレビュー CRITICAL反映: 0件保存は「既存データを全て削除する」操作として許可する
    // (既存データも無いのに0件保存しても無意味なのでその場合のみ拒否)
    if (filledRows.length === 0 && (existingQuery.data?.entries.length ?? 0) === 0) {
      toast.error('入力内容がありません');
      return;
    }
    const targetIds = filledRows.map((r) => r.target_id);
    if (new Set(targetIds).size !== targetIds.length) { toast.error(`同じ${TARGET_LABEL[sourceType]}が複数行に重複しています`); return; }
    setConfirmOpen(true);
  };

  const doSave = async () => {
    if (!storeId || !selectedStore || !monthDate) return;

    setSaving(true);
    setConfirmOpen(false);
    try {
      const result = await apiFetch<DirectSaveResponse>(`/lite-csv-import/${sourceType}/direct-save`, {
        method: 'POST',
        body: JSON.stringify({
          node_id: storeId,
          month: monthDate,
          entries: filledRows.map((r) => ({ target_id: r.target_id, amount: Number(r.amount) })),
        }),
      });
      if (result.status !== 'completed') {
        toast.error(`保存に失敗しました: ${result.error ?? ''}`);
        return;
      }

      toast.success(filledRows.length > 0 ? `保存しました (${filledRows.length} 件)` : 'この店舗・月のデータを全て削除しました');
      loadedKeyRef.current = null;
      qc.invalidateQueries({ queryKey: ['lite-manual-entry-records', sourceType, storeId, monthDate] });
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-h1">データ手入力</h1>
        <p className="text-sm text-neutral-sub mt-1">月次PL・仕入先別コスト・枠外調整をCSVを介さず画面から直接入力・更新・削除します (Lite版データモデル)。</p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="le-store" className="block text-xs text-neutral-sub mb-1">店舗 *</label>
          <select id="le-store" value={storeId} onChange={(e) => setStoreId(e.target.value)}
            className="min-w-[180px] px-3 py-2 border border-neutral-line rounded text-sm focus-ring">
            <option value="">選択してください</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.store_name}{s.status === 'closed' ? '（停止中）' : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="le-month" className="block text-xs text-neutral-sub mb-1">対象月 *</label>
          <MonthPicker id="le-month" aria-label="対象月 (必須)" value={month} onChange={setMonth}
            className="px-3 py-2 border border-neutral-line rounded text-sm focus-ring" />
        </div>
        <div>
          <label className="block text-xs text-neutral-sub mb-1">データ種別</label>
          <div className="flex gap-2">
            {(Object.keys(SOURCE_LABEL) as SourceType[]).map((t) => (
              <button key={t} type="button" onClick={() => setSourceType(t)}
                className={`px-3 py-2 rounded border text-sm ${sourceType === t ? 'border-brand-red text-brand-red bg-surface-sunken' : 'border-neutral-line text-neutral-sub'}`}>
                {SOURCE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
        {storeId && monthDate && (
          <div className="text-xs text-neutral-sub flex items-center gap-1 basis-full">
            <Info size={14} />
            {existingQuery.isLoading || existingQuery.isFetching
              ? '既存データを確認中…'
              : `この店舗・月・種別には現在 ${existingQuery.data?.entries.length ?? 0} 件のデータがあります（下表に読込済み。保存すると下表に無い行は削除されます）`}
          </div>
        )}
      </div>

      {storeId && monthDate ? (
        <>
          <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-panel text-neutral-sub">
                  <tr>
                    <th className="w-8"></th>
                    <th className="text-left px-2 py-2">{TARGET_LABEL[sourceType]}</th>
                    <th className="text-left px-2 py-2">金額（円）</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-neutral-sub py-6">「行を追加」ボタンで入力を開始してください</td></tr>
                  )}
                  {rows.map((row) => {
                    const usedElsewhere = new Set(rows.filter((r) => r.rowKey !== row.rowKey).map((r) => r.target_id));
                    const options = masterList.filter((m) => m.id === row.target_id || !usedElsewhere.has(m.id));
                    return (
                      <tr key={row.rowKey} className="border-t border-neutral-line">
                        <td className="px-1">
                          <button type="button" onClick={() => removeRow(row.rowKey)}
                            className="text-neutral-sub hover:text-warning-text p-1" aria-label="この行を削除"><Trash2 size={14} /></button>
                        </td>
                        <td className="px-1 py-1">
                          <select value={row.target_id} onChange={(e) => updateRow(row.rowKey, 'target_id', e.target.value)}
                            className="w-full min-w-[160px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring">
                            <option value="">選択してください</option>
                            {options.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" value={row.amount} onChange={(e) => updateRow(row.rowKey, 'amount', e.target.value)}
                            className="w-full min-w-[120px] px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button type="button" onClick={addRow} className="mt-3 flex items-center gap-1 text-sm text-brand-red hover:underline">
                <Plus size={14} /> 行を追加
              </button>
            </div>

            <div className="flex justify-end pt-2 border-t border-neutral-line">
              <button type="button" onClick={openConfirm} disabled={saving || !dataReady}
                title={!dataReady ? '既存データの読込中は保存できません' : undefined}
                className="px-5 py-2 rounded bg-brand-red text-white text-sm font-medium hover:bg-brand-red-hover disabled:opacity-50">
                {saving ? '保存中…' : '保存する'}
              </button>
            </div>
          </div>

          <AdjustmentsSection storeId={storeId} monthDate={monthDate} />
        </>
      ) : (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          店舗と対象月を選択すると入力欄が表示されます
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={`${SOURCE_LABEL[sourceType]}を保存しますか？`}
        destructive={removedCount > 0}
        confirmLabel="保存する"
        loading={saving}
        onClose={() => { if (!saving) setConfirmOpen(false); }}
        onConfirm={doSave}
        message={
          <div className="space-y-2">
            <p>{selectedStore?.store_name} の {month} 分として、<strong>{filledRows.length} 件</strong>を保存します。</p>
            <p className="text-xs text-neutral-sub">
              この操作はこの店舗・月・種別の既存データを全て置き換えます（現在 {existingQuery.data?.entries.length ?? 0} 件 → 保存後 {filledRows.length} 件）。
              上の入力欄に表示されていない行は削除されます。
            </p>
            {removedCount > 0 && (
              <div className="p-2 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex items-start gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                {removedCount} 件が削除されます。よろしいですか？
              </div>
            )}
          </div>
        }
      />
    </div>
  );
}
